import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spiner } from '../../components';
import { InputForm } from '../../components';

export type RoleFormMode = 'create' | 'rename';

interface RoleFormModalProps {
    modalId?: string;
    mode: RoleFormMode;
    /** Prefill for rename (current role label). */
    initialLabel?: string;
    isOpen?: boolean;
    onClose?: () => void;
    onSubmit: (label: string, previousLabel?: string) => Promise<boolean | void> | boolean | void;
}

/**
 * Dark Bootstrap modal (same visual language as InviteUser) for creating or renaming a process role.
 */
const RoleFormModal: React.FC<RoleFormModalProps> = ({
    modalId = 'roleFormModal',
    mode,
    initialLabel = '',
    isOpen,
    onClose,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const [label, setLabel] = useState(initialLabel);
    const [isSubmit, setIsSubmit] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setLabel(initialLabel);
            setError('');
            setIsSubmit(false);
            const timer = window.setTimeout(() => {
                const el = document.getElementById(modalId);
                if (!el) return;
                const Modal = (window as any).bootstrap?.Modal;
                if (Modal) {
                    Modal.getOrCreateInstance(el).show();
                } else {
                    const btn = document.createElement('button');
                    btn.setAttribute('data-bs-toggle', 'modal');
                    btn.setAttribute('data-bs-target', `#${modalId}`);
                    document.body.appendChild(btn);
                    btn.click();
                    btn.remove();
                }
            }, 30);
            return () => window.clearTimeout(timer);
        }
        return undefined;
    }, [isOpen, initialLabel, modalId]);

    useEffect(() => {
        const el = document.getElementById(modalId);
        if (!el) return;
        const onHidden = () => {
            setLabel('');
            setError('');
            setIsSubmit(false);
            onClose?.();
        };
        el.addEventListener('hidden.bs.modal', onHidden);
        return () => el.removeEventListener('hidden.bs.modal', onHidden);
    }, [modalId, onClose]);

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        const next = label.trim();
        if (!next) {
            setError(t('views.processInterview.createRolePlaceholder', 'New role name'));
            return;
        }
        if (mode === 'rename' && next === (initialLabel || '').trim()) {
            closeRef.current?.click();
            return;
        }
        setIsSubmit(true);
        setError('');
        try {
            const result = await onSubmit(next, mode === 'rename' ? initialLabel : undefined);
            if (result === false) {
                setIsSubmit(false);
                return;
            }
            closeRef.current?.click();
        } catch {
            setError(t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
        }
        setIsSubmit(false);
    };

    const title =
        mode === 'create'
            ? t('views.processInterview.createRole', 'Add role')
            : t('views.processInterview.renameRole', 'Rename role');

    return (
        <div className="modal" data-bs-backdrop="static" id={modalId} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content bg-dark">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">{title}</h5>
                        <button
                            type="button"
                            className="btn-close bg-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        />
                    </div>
                    <div className="modal-body">
                        {error ? (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        ) : null}
                        {mode === 'rename' && initialLabel ? (
                            <p className="text-theme small mb-3">
                                {t('views.processInterview.currentRole', 'Current role')}:{' '}
                                <strong>{initialLabel}</strong>
                            </p>
                        ) : null}
                        <InputForm
                            inputType="text"
                            inputLabel={t('navigation.enterRole')}
                            inputName="role-form-label"
                            inputValue={label}
                            isFloatingLabel={true}
                            labelClass="text-theme"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setLabel(e.target.value)
                            }
                            inputClass="bg-dark text-theme"
                        />
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                        >
                            {t('words.close')}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                            {isSubmit ? (
                                <Spiner className="spinner-border-sm" />
                            ) : mode === 'create' ? (
                                t('words.add')
                            ) : (
                                t('words.save')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoleFormModal;
